import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../lib/adminAPI';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import { Shield, UserPlus, Trash2, Mail } from 'lucide-react';

const AdminManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const load = async () => {
    setLoading(true);
    try { setAdmins(await adminAPI.listAdmins()); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const promote = async (e) => {
    e.preventDefault();
    const val = email.trim();
    if (!val) return;
    setAdding(true);
    try {
      const r = await adminAPI.promoteAdmin(val);
      toast({ title: 'Admin added', description: `${r.user?.email} is now an admin.` });
      setEmail('');
      load();
    } catch (err) {
      toast({ title: 'Could not add admin', description: err?.response?.data?.detail || 'Try again' });
    } finally { setAdding(false); }
  };

  const demote = async (a) => {
    if (!window.confirm(`Remove admin access from ${a.email}?`)) return;
    try {
      await adminAPI.demoteAdmin(a.id);
      toast({ title: 'Admin removed', description: `${a.email} is now a regular user.` });
      load();
    } catch (err) {
      toast({ title: 'Could not remove admin', description: err?.response?.data?.detail || 'Try again' });
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-management-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Admins</h1>
        <p className="text-sm text-gray-400">Grant or revoke admin (CRM) access for any account.</p>
      </div>

      {/* Add admin */}
      <form onSubmit={promote} className="p-5 rounded-xl border border-[#1a2823] bg-[#0a1210] flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1">
          <label className="text-[10px] text-gray-400 uppercase tracking-wider">Add admin by email</label>
          <div className="mt-1 relative">
            <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"/>
            <input
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="user@email.com"
              data-testid="promote-admin-email"
              className="w-full bg-[#050807] border border-[#1a2823] rounded-lg pl-9 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#00e676]"
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={adding || !email.trim()}
          data-testid="promote-admin-btn"
          className="btn-green px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50"
        >
          <UserPlus size={14}/> {adding ? 'Adding…' : 'Make Admin'}
        </button>
      </form>

      {/* Admins list */}
      <div className="rounded-xl border border-[#1a2823] bg-[#0a1210] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#141f1b] flex items-center gap-2">
          <Shield size={14} className="text-[#00e676]"/>
          <span className="text-sm font-semibold text-white">Current Admins</span>
          <span className="text-xs text-gray-500">({admins.length})</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-3">Admin</th><th>Email</th><th>Since</th><th className="text-right pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="4" className="py-6 text-center text-sm text-gray-500">Loading…</td></tr>}
              {!loading && admins.length === 0 && <tr><td colSpan="4" className="py-10 text-center text-sm text-gray-500">No admins yet</td></tr>}
              {admins.map(a => (
                <tr key={a.id} className="border-t border-[#141f1b] hover:bg-[#0f1a17]" data-testid={`admin-row-${a.email}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-500/15 border border-purple-500/30 flex items-center justify-center text-purple-400 text-xs font-bold">{a.name?.[0]?.toUpperCase() || 'A'}</div>
                      <div>
                        <div className="text-sm text-white font-semibold flex items-center gap-2">
                          {a.name || 'Admin'}
                          {a.is_self && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#00e676]/15 text-[#00e676] font-bold">YOU</span>}
                        </div>
                        <div className="text-[10px] text-purple-400 font-bold">ADMIN</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-gray-300">{a.email}</td>
                  <td className="text-xs text-gray-400 mono">{a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}</td>
                  <td className="text-right pr-4">
                    <button
                      onClick={() => demote(a)}
                      disabled={a.is_self}
                      data-testid={`demote-admin-${a.email}`}
                      title={a.is_self ? 'You cannot remove your own access' : 'Remove admin access'}
                      className="p-1.5 rounded border border-[#1a2823] text-gray-400 hover:text-red-400 hover:border-red-500/40 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Trash2 size={12}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminManagement;
